import type { HTMLAttributes, ReactNode } from 'react';

import React from 'react';

import { InfoCircleIcon } from '../../icons';
import { classNames } from '../../utils/class-names';

import styles from './selection-input-layout.module.css';

export type Variant = 'ghost';

export type SelectionInputLayoutProps = {
  variant?: Variant;
  hintMessage?: ReactNode;
  errorMessage?: ReactNode;
  checked?: boolean;
  disabled?: boolean;
  inputElement?: ReactNode;
} & HTMLAttributes<HTMLLabelElement>;

/**
 * @param {Variant} [variant] The variant of the selection input layout.
 * @default undefined
 * @param {ReactNode} [hintMessage] Hint message to be displayed.
 * @default undefined
 * @param {ReactNode} [errorMessage] Error message to be displayed.
 * @default undefined
 * @param {boolean} [checked] Indicates if the input is checked.
 * @default false
 * @param {boolean} [disabled] Indicates if the input is disabled.
 * @default false
 * @param {ReactNode} [inputElement] Custom input element to be rendered.
 * @default undefined
 */
export function SelectionInputLayout({
  variant,
  hintMessage,
  errorMessage,
  checked,
  disabled,
  className,
  children,
  inputElement,
  ...props
}: SelectionInputLayoutProps) {
  return (
    <div
      className={classNames(
        styles.wrapper,
        variant && styles[variant],
        checked && styles.checked,
        disabled && styles.disabled,
        className
      )}
    >
      <label
        className={classNames(styles.labelWrapper, checked && styles.checked, disabled && styles.disabled)}
        {...props}
      >
        {inputElement}

        {hintMessage && <span className={styles.hintMessage}>{hintMessage}</span>}

        {errorMessage && (
          <span className={styles.errorMessage}>
            <InfoCircleIcon />
            {errorMessage}
          </span>
        )}
      </label>

      {checked && children && <div className={styles.expandedContent}>{children}</div>}
    </div>
  );
}
